import { Checkbox, Input } from '@headlessui/react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { Controller, useFieldArray } from 'react-hook-form';

import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { faCheck, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Task } from 'models/Memo';
import { v4 as uuidv4 } from 'uuid';
import { useMemoFormContext } from '../context/FormContext';
import TitleField from '../fields/TitleField';
import DescriptionField from '../fields/DescriptionField';
import { useTranslations } from 'next-intl';

type TaskListItemProps = {
  value: Task;
  isDragging?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  onChange?: (val: Task) => void;
  onRemove?: () => void;
};

const TaskListItem = ({
  value,
  isDragging,
  dragHandleProps,
  onChange,
  onRemove,
}: TaskListItemProps) => {
  const t = useTranslations();

  return (
    <div className={styles.taskListItem.root(isDragging)}>
      <div {...dragHandleProps} className={styles.taskListItem.gripHandle}>
        <FontAwesomeIcon
          icon={faGripDotsVertical}
          className={styles.taskListItem.gripIcon}
        />
      </div>

      <Checkbox
        checked={value.isCompleted}
        onChange={(checked) => {
          onChange({ ...value, isCompleted: checked });
        }}
        className={styles.taskListItem.checkbox.outer}
      >
        <span className={styles.taskListItem.checkbox.inner(value.isCompleted)}>
          {value.isCompleted && (
            <FontAwesomeIcon
              icon={faCheck}
              className={styles.taskListItem.checkIcon}
            />
          )}
        </span>
      </Checkbox>

      <div className={styles.taskListItem.input.root}>
        {value.isCompleted ? (
          <p className={styles.taskListItem.input.text}>{value.content}</p>
        ) : (
          <Input
            value={value.content}
            placeholder={t('memoComposer.tasks.placeholder')}
            onChange={(e) => onChange({ ...value, content: e.target.value })}
            className={styles.taskListItem.input.field}
          />
        )}
      </div>

      <button
        type="button"
        className={styles.taskListItem.removeButton.root(!!onRemove)}
        onClick={onRemove}
      >
        <FontAwesomeIcon
          icon={faXmark}
          className={styles.taskListItem.removeButton.icon}
        />
      </button>
    </div>
  );
};

const TaskListForm: React.FC = () => {
  const t = useTranslations();
  const { control, isTitleVisible, isDescriptionVisible } =
    useMemoFormContext();
  const { fields, append, remove, move } = useFieldArray<{
    tasks: Task[];
  }>({ control, name: 'tasks' });

  const handleAddTask = () => {
    append({ isCompleted: false, content: '', id: uuidv4() });
  };

  const handleRemoveTask = (index: number) => {
    remove(index);
  };

  const handleDragEnd = (props: DropResult) => {
    const { source, destination } = props;
    if (!destination) return;
    if (source.index === destination.index) return;

    move(source.index, destination.index);
  };

  return (
    <div>
      {isTitleVisible && (
        <div className={styles.title.root}>
          <TitleField />
        </div>
      )}

      {isDescriptionVisible && (
        <div className={styles.title.root}>
          <DescriptionField />
        </div>
      )}

      <DragDropContext
        onDragEnd={handleDragEnd}
        autoScrollerOptions={{ disabled: true }}
      >
        <Droppable droppableId="task-list-form">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={styles.items.root}
            >
              {fields.map((field, index) => (
                <Draggable key={field.id} draggableId={field.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={provided.draggableProps.style}
                    >
                      <Controller
                        control={control}
                        name={`tasks.${index}`}
                        render={({ field: { onChange, value } }) => (
                          <TaskListItem
                            value={value}
                            isDragging={snapshot.isDragging}
                            onChange={onChange}
                            dragHandleProps={provided.dragHandleProps}
                            onRemove={
                              fields.length === 1
                                ? undefined
                                : () => handleRemoveTask(index)
                            }
                          />
                        )}
                        rules={{
                          validate: (value) => {
                            if (!value?.content)
                              return t('memoComposer.tasks.required');
                            return true;
                          },
                        }}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {/* need to render this placeholder to prevent height recalculation */}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <button
        type="button"
        onClick={handleAddTask}
        className={styles.addButton.root}
      >
        <span className={styles.addButton.gripSlot} />
        <span className={styles.addButton.iconSlot}>
          <span className={styles.addButton.icon}>
            <FontAwesomeIcon icon={faPlus} />
          </span>
        </span>
        <span className={styles.addButton.label}>
          {t('memoComposer.tasks.add')}
        </span>
        <span className={styles.addButton.removeSlot} />
      </button>
    </div>
  );
};

export default TaskListForm;

const styles = {
  taskListItem: {
    root: (isDragging?: boolean) =>
      classNames(
        'group flex items-center gap-2.5 py-[7px] px-1 rounded-lg transition-colors',
        isDragging
          ? 'bg-accent-tint shadow-lg outline-2 outline-dashed outline-mf-blue/55'
          : 'hover:bg-accent-tint',
      ),
    checkbox: {
      outer:
        'shrink-0 flex items-center justify-center size-[22px] cursor-pointer',
      inner: (isChecked: boolean) =>
        classNames(
          'flex items-center justify-center w-[18px] h-[18px] rounded-[5px] transition-colors',
          isChecked
            ? 'bg-accent-fill'
            : 'bg-transparent border-[1.5px] border-ink-primary/25',
        ),
    },
    checkIcon: 'w-[10px] h-[10px] text-white',
    gripHandle: classNames(
      'shrink-0 w-[14px] flex items-center justify-center',
      'opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing',
    ),
    gripIcon: 'text-[10px] text-ink-tertiary',
    input: {
      root: 'flex items-center grow',
      field: classNames(
        'block w-full border-none bg-transparent py-0 px-0 text-sm',
        'focus:ring-0',
      ),
      text: 'w-full text-sm text-ink-tertiary',
    },
    removeButton: {
      root: (visible: boolean) =>
        classNames(
          'shrink-0 flex items-center justify-center size-[22px] transition-opacity',
          visible ? 'opacity-0 group-hover:opacity-100' : 'invisible',
        ),
      icon: 'text-[11px] font-semibold text-ink-tertiary',
    },
  },
  title: {
    root: 'w-full mb-3',
    input: classNames(
      'mr-3 block w-full rounded-lg border-none bg-transparent py-1.5 pl-0 pr-3 text-ink-primary text',
      'placeholder-ink-tertiary focus:ring-0',
    ),
  },
  addButton: {
    root: classNames(
      'flex w-full items-center gap-2.5 py-[7px] px-1 rounded-lg',
      'text-left transition-colors hover:bg-accent-tint',
    ),
    gripSlot: 'shrink-0 w-[14px]',
    iconSlot: 'shrink-0 flex items-center justify-center size-[22px]',
    icon: classNames(
      'flex items-center justify-center w-[18px] h-[18px] rounded-[5px] text-[10px] font-semibold',
      'border-[1.2px] border-dashed border-stroke text-ink-tertiary',
    ),
    label: 'flex-1 text-sm text-ink-tertiary',
    removeSlot: 'shrink-0 size-[22px]',
  },
  items: {
    root: 'flex flex-col',
  },
};
